import { useEffect, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Briefcase, User, Eye } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const LiveViewerModal = lazy(() => import('./LiveViewerModal'));

// Admin-only ("solo Andy") live board: who is logged into the helpdesk RIGHT NOW
// (agents + clients) and what page they're on. Polls every 4s. Renders nothing for
// anyone else — no trace. Clicking a card opens the live screen mirror (Phase 2).
const LIVE_VIEW_USER_IDS = ['eafdff80-02d2-4eef-9e5c-19f57761b85d']; // Andy

// Turn a route path into a friendly label.
function prettyPath(path, homeLabel) {
  if (!path) return '—';
  const clean = path.split('?')[0].replace(/^\/+/, '');
  if (!clean) return homeLabel;
  const seg = clean.split('/');
  const head = seg[0].replace(/-/g, ' ');
  return head.charAt(0).toUpperCase() + head.slice(1) + (seg[1] ? ' ›' : '');
}

export default function LiveSessionsPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fmtIdle = (s) => (s < 60 ? t('live.now') : t('live.agoMin', { count: Math.floor(s / 60) }));
  const allowed = !!user && LIVE_VIEW_USER_IDS.includes(user.id);
  const [data, setData] = useState(null);
  const [watching, setWatching] = useState(null); // { userId, name }

  useEffect(() => {
    if (!allowed) return undefined;
    let alive = true;
    const load = () => api.get('/live/sessions').then((r) => { if (alive) setData(r.data); }).catch(() => {});
    load();
    const iv = setInterval(load, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [allowed]);

  if (!allowed) return null;
  const sessions = data?.sessions || [];

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Radio size={18} style={{ color: '#22c55e' }} />
          {t('live.title')}
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: '#22c55e',
            boxShadow: '0 0 0 0 rgba(34,197,94,.6)', animation: 'livePulse 1.6s infinite',
          }} />
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--c-text-muted)' }}>
          <span><Briefcase size={12} style={{ marginRight: 3, verticalAlign: -1 }} />{t('live.agentsCount', { count: data?.agents ?? 0 })}</span>
          <span><User size={12} style={{ marginRight: 3, verticalAlign: -1 }} />{t('live.clientsCount', { count: data?.clients ?? 0 })}</span>
          <strong style={{ color: 'var(--c-text)' }}>{t('live.connectedCount', { count: data?.count ?? 0 })}</strong>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('live.nobody')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
          {sessions.map((s) => {
            const isClient = s.role === 'client';
            const col = isClient ? '#8b5cf6' : '#3b82f6';
            // s.live === false → online per presence but no live page in Redis (stale bundle /
            // hidden tab). Show it, but there's no page to mirror, so no watch affordance.
            const canWatch = s.live !== false;
            return (
              <div key={s.userId}
                onClick={canWatch ? () => setWatching({ userId: s.userId, name: s.name }) : undefined}
                title={canWatch ? t('live.watchTitle') : t('live.onlineNoPage')}
                style={{
                  border: '1px solid var(--c-border)', borderRadius: 10, padding: 12,
                  borderLeft: `3px solid ${col}`, background: 'var(--c-surface-2)',
                  cursor: canWatch ? 'pointer' : 'default', opacity: canWatch ? 1 : 0.72,
                }}
                onMouseEnter={(e) => { if (canWatch) e.currentTarget.style.boxShadow = `0 2px 10px rgba(0,0,0,.1)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{s.name}</span>
                  {canWatch && <Eye size={13} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />}
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                    background: `${col}22`, color: col, textTransform: 'uppercase',
                  }}>{isClient ? t('live.clientBadge') : (s.role || t('live.agentBadge'))}</span>
                </div>
                <div style={{ fontSize: 12, color: canWatch ? 'var(--c-text)' : 'var(--c-text-muted)', marginBottom: 2, fontStyle: canWatch ? 'normal' : 'italic' }}>
                  {canWatch ? prettyPath(s.path, t('live.home')) : t('live.onlineNoPage')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.companyName || (isClient ? '—' : 'AyudaTecno')}</span>
                  <span style={{ flexShrink: 0 }}>{fmtIdle(s.idle_seconds)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes livePulse { 0%{box-shadow:0 0 0 0 rgba(34,197,94,.5)} 70%{box-shadow:0 0 0 6px rgba(34,197,94,0)} 100%{box-shadow:0 0 0 0 rgba(34,197,94,0)} }`}</style>
      {watching && (
        <Suspense fallback={null}>
          <LiveViewerModal targetUserId={watching.userId} targetName={watching.name} onClose={() => setWatching(null)} />
        </Suspense>
      )}
    </div>
  );
}
